import React, { useEffect, useState } from 'react';
import { Phone, Clock, Coins, Mic, MessageSquare, TrendingUp, RefreshCw } from 'lucide-react';
import apiClient from '../api/client';

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtDur(s) {
  if (!s) return '0s';
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}
function fmtCost(n) {
  if (!n) return '$0.0000';
  return '$' + Number(n).toFixed(4);
}
function fmtTokens(n) {
  if (!n) return '0';
  return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n);
}

export default function VoiceCallLogs() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    apiClient.get('/voice/logs')
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const totals = data?.totals || {};
  const logs   = data?.logs   || [];

  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Phone className="w-6 h-6 text-blue-600" /> Voice Agent Call Logs
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Cost tracking per call — GPT‑4o tokens + ACS usage</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 border rounded-lg px-3 py-1.5 hover:bg-blue-50 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Total calls',      value: totals.TotalCalls    ?? '—', icon: Phone,        color: 'blue' },
          { label: 'Total duration',   value: fmtDur(totals.TotalSeconds), icon: Clock,        color: 'indigo' },
          { label: 'Input tokens',     value: fmtTokens(totals.TotalInputTokens),  icon: Mic,   color: 'violet' },
          { label: 'Output tokens',    value: fmtTokens(totals.TotalOutputTokens), icon: MessageSquare, color: 'purple' },
          { label: 'Total cost (est)', value: fmtCost(totals.TotalCostUSD), icon: Coins,       color: 'emerald' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className={`bg-${color}-50 border border-${color}-200 rounded-xl p-4`}>
            <div className={`text-${color}-600 mb-1`}><Icon className="w-4 h-4" /></div>
            <div className="text-xl font-bold text-gray-800">{loading ? '…' : value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Cost note */}
      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6 text-xs text-amber-800">
        <TrendingUp className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
        <span>
          <strong>Cost model:</strong> GPT‑4o $2.50/1M input + $10.00/1M output tokens &nbsp;·&nbsp;
          ACS $0.028/min (call automation + STT + TTS). Estimates only — check Azure Cost Management for exact billing.
        </span>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-4 text-sm">{error}</div>
      )}

      {/* Table */}
      {!loading && logs.length === 0 && !error && (
        <div className="text-center py-16 text-gray-400">
          <Phone className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No calls logged yet</p>
          <p className="text-sm">Call logs appear here after your first voice agent call.</p>
        </div>
      )}

      {logs.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Caller','Started','Duration','Turns','Input tokens','Output tokens','Est. cost'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map(log => (
                <tr key={log.CallLogID} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800">{log.CallerName}</td>
                  <td className="px-4 py-3 text-gray-600">{fmt(log.StartedAt)}</td>
                  <td className="px-4 py-3 text-gray-600">{fmtDur(log.DurationSeconds)}</td>
                  <td className="px-4 py-3 text-gray-600">{log.TurnCount}</td>
                  <td className="px-4 py-3 text-gray-600">{fmtTokens(log.InputTokens)}</td>
                  <td className="px-4 py-3 text-gray-600">{fmtTokens(log.OutputTokens)}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-700">{fmtCost(log.EstimatedCostUSD)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
